/**
 * Properties of a Recipe.
 *
 * @note The interfaces in this file are more or less copied from the supabase generated types in the users-svc.
 */

import {
  ValidationError,
  addMethod,
  array,
  number,
  object,
  string,
  type Flags,
  type InferType,
} from "yup"
import { RecipeSchemaNameMaxLength } from "./constants"
import {
  RecipeComponentSchema,
  RecipeComponentCreateSchema,
  type RecipeComponent,
} from "./RecipeComponent"
import { RecipePortionSchema } from "./RecipePortion"
import {
  RecipesAusladegruppenSchema,
  RecipesAusladegruppenCreateSchema,
  type RecipesAusladegruppen,
} from "./RecipesAusladegruppen"

// Add a `unique` method to arrays
// From: https://github.com/jquense/yup/issues/345
addMethod(
  array,
  "uniqueProperty",
  function (
    propertyName: string,
    message: string = "${path} may not have duplicates"
  ) {
    return this.test(
      "uniqueProperty",
      message,
      (list: any[] | undefined, context) => {
        // console.log("🔬 evaluating unique constraint on ", list)
        if (!list) {
          return true
        }
        const errors: ValidationError[] = []
        const duplicateProperties = list
          .filter(
            (e, i) =>
              list.findIndex((e2) => e2[propertyName] === e[propertyName]) !== i
          )
          .map((e) => e[propertyName])
        for (let i = 0; i < list.length; i += 1) {
          const element = list[i]
          if (
            element[propertyName] !== "" &&
            duplicateProperties.includes(element[propertyName])
          ) {
            errors.push(
              new ValidationError(
                message,
                element,
                `${context.path}[${i}].${propertyName}`
              )
            )
          }
        }
        if (errors.length > 0) {
          // console.log("❌❌❌", errors)
          return context.createError({ message: () => errors })
        }

        return true
      }
    )
  }
)

// Declare the `uniqueProperty` method, so TS knows
// From: https://github.com/jquense/yup/issues/345
declare module "yup" {
  interface ArraySchema<
    TIn extends any[] | null | undefined,
    TContext,
    TDefault = undefined,
    TFlags extends Flags = "",
  > {
    uniqueProperty(
      propertyName: string,
      message?: string
    ): ArraySchema<TIn, TContext, TDefault, TFlags>
  }
}

const CreatedPropsSchema = object({
  created_at: string().required(),
  created_by: string().required(),
})
type CreatedProps = InferType<typeof CreatedPropsSchema>
type CreatedPropsKeys = keyof CreatedProps
const UpdatedPropsSchema = object({
  updated_at: string().required(),
  updated_by: string().required(),
})
type UpdatedProps = InferType<typeof UpdatedPropsSchema>
type UpdatedPropsKeys = keyof UpdatedProps

/**
 * Summary of the schemas/types and their differences
 *   - Recipe[Schema] - a full recipe (from a schema)
 *   - RecipeCreate[Schema] - for creation of a new recipe (from a schema)
 *   - RecipeUpdate[Schema] - for update of an existing recipe (from a schema)
 *   - MyNewRecipeSchema - for creation of a new recipe (manual)
 */
export const RecipeSchema = object({
  created_at: string().required(),
  created_by: string().required(),
  id: string().required(),
  name: string().required().max(RecipeSchemaNameMaxLength),
  org_id: string().required(),
  updated_at: string().defined(),
  updated_by: string().defined(),
  deleted_at: string().nullable(),
  deleted_by: string().nullable(),
  portions: array(RecipePortionSchema).nullable(),
  RecipeComponents: array(RecipeComponentSchema).required(),
  RecipesAusladegruppen: array(RecipesAusladegruppenSchema).required(),
})

/**
 * Create-DTO: All data that's transferred from FE to BE for a create request.
 */
export const RecipeCreateSchema = RecipeSchema.omit([
  "created_at",
  "created_by",
  "updated_at",
  "updated_by",
  "deleted_at",
  "deleted_by",
  "RecipeComponents",
  "RecipesAusladegruppen",
]).concat(
  object({
    id: string().optional(),
    RecipeComponents: array(RecipeComponentCreateSchema).required(),
    RecipesAusladegruppen: array(RecipesAusladegruppenCreateSchema).required(),
  })
)

/**
 * Update-DTO: All data that's transferred from FE to BE for a update request.
 *
 * @note Of course, `id` is required for an update. It is taken from the path.
 * @note `id` and `org_id` cannot be changed in the FE, so there's no point including them in the request.
 */
export const RecipeUpdateSchema = RecipeSchema.omit([
  //"org_id", # TO CLARIFY: Warum kann org_id bei Update wegfallen?
  "created_at",
  "created_by",
  "updated_at",
  "updated_by",
  "deleted_at",
  "deleted_by",
])

export const RecipeForDeviceSchema = RecipeSchema.concat(
  object({
    parent_id: string().nullable(),
    percentage: number().positive().nullable(),
  })
).omit(["portions"])

export type RecipeDto = InferType<typeof RecipeSchema>
export type RecipeCreateDto = InferType<typeof RecipeCreateSchema>
export type RecipeUpdateDto = InferType<typeof RecipeUpdateSchema>

export type RecipeForDeviceDto = InferType<typeof RecipeForDeviceSchema>

// export type RecipeDeleteDto = InferType<typeof UpdatedRecipeSchema>

/** @deprecated */
type _MyNewRecipesAusladegruppen = Omit<
  RecipesAusladegruppen,
  "recipe_id" | CreatedPropsKeys | UpdatedPropsKeys
>
/** @deprecated */
type _MyNewRecipeComponent = Omit<
  RecipeComponent,
  "recipe_id" | CreatedPropsKeys | UpdatedPropsKeys
>
/**
 * @ deprecated
 * Used by fe-api ;-(
 */
export type MyNewRecipe = Omit<
  RecipeDto,
  | "id"
  | CreatedPropsKeys
  | UpdatedPropsKeys
  | "RecipesAusladegruppen"
  | "RecipeComponents"
> /* & Partial<UpdatedProps> */ & {
  RecipesAusladegruppen: _MyNewRecipesAusladegruppen[]
  RecipeComponents: _MyNewRecipeComponent[]
}

// type Prettify<T> = {
//   [K in keyof T]: T[K];
// } & {};
// type asdf = Prettify<RecipeForDeviceDto>
